// ARCHIVO: src/LoginModal.js
// Se muestra dentro de la vista del cliente (.cv-root), así que usa sus
// tokens de diseño por variables CSS (--cv-*) y sus clases (cv-input,
// cv-display): sigue el modo claro/oscuro y el color de cada barbería.
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion, useDragControls } from 'framer-motion';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile
} from 'firebase/auth';

const DANGER = '#E5484D';
const OK = '#2FA36B';

const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#ffffff';
  const hex = hexcolor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) || 0;
  const g = parseInt(hex.substr(2, 2), 16) || 0;
  const b = parseInt(hex.substr(4, 2), 16) || 0;
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#0f172a' : '#ffffff';
};

// Errores de Firebase en palabras del cliente. `null` = no mostrar nada
// (por ejemplo, si cerró la ventana de Google a propósito).
const AUTH_ERRORS = {
  'auth/email-already-in-use': 'Este correo ya tiene una cuenta. Probá ingresar.',
  'auth/invalid-credential': 'El correo o la contraseña no coinciden.',
  'auth/wrong-password': 'El correo o la contraseña no coinciden.',
  'auth/user-not-found': 'No hay ninguna cuenta con este correo.',
  'auth/invalid-email': 'Ese correo no es válido.',
  'auth/weak-password': 'La contraseña necesita al menos 6 caracteres.',
  'auth/too-many-requests': 'Demasiados intentos. Esperá unos minutos y probá de nuevo.',
  'auth/network-request-failed': 'Sin conexión. Revisá tu internet.',
  'auth/popup-closed-by-user': null,
  'auth/cancelled-popup-request': null,
};

const COPY = {
  login: { title: 'Bienvenido', subtitle: 'Ingresá para ver y manejar tus turnos.', cta: 'Ingresar' },
  register: { title: 'Crear cuenta', subtitle: 'Guardamos tus datos y reservás en segundos.', cta: 'Crear mi cuenta' },
  recover: { title: 'Recuperar', subtitle: 'Te mandamos un enlace a tu correo para crear una contraseña nueva.', cta: 'Enviar enlace' },
};

const revealProps = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
  style: { overflow: 'hidden' },
};

export const LoginModal = ({ show, onClose, brandColor = '#F59E0B', isDarkMode = true, shopName, logoUrl }) => {
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const auth = getAuth();
  const contrastText = getContrastColor(brandColor);
  const reduceMotion = useReducedMotion();

  // Mobile: hoja que sube desde abajo y se cierra arrastrándola hacia abajo
  // desde el encabezado. Desktop: tarjeta centrada.
  const dragControls = useDragControls();
  const [isMobile] = useState(() => typeof window !== 'undefined' && !window.matchMedia('(min-width: 640px)').matches);
  const sheetHidden = reduceMotion ? { opacity: 0 } : isMobile ? { y: '100%' } : { opacity: 0, scale: 0.97 };
  const sheetTransition = isMobile && !reduceMotion
    ? { duration: 0.35, ease: [0.32, 0.72, 0, 1] }
    : { duration: 0.2, ease: [0.23, 1, 0.32, 1] };

  const resetMessages = () => { setError(''); setSuccessMessage(''); };
  const resetForm = () => {
    resetMessages();
    setEmail('');
    setPassword('');
    setName('');
    setShowPassword(false);
    setLoading(false);
    setGoogleLoading(false);
  };

  const handleClose = () => {
    resetForm();
    setView('login');
    onClose();
  };

  // Al cambiar entre "Ingresar" y "Crear cuenta" se conserva lo que ya
  // escribió (correo y contraseña); solo se limpian los avisos.
  const switchView = (next) => {
    resetMessages();
    setView(next);
  };

  // Escape cierra en desktop.
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const authErrorMessage = (err) => {
    if (err.code && err.code in AUTH_ERRORS) return AUTH_ERRORS[err.code];
    if (!err.code && err.message) return err.message; // errores propios (throw new Error)
    return 'Algo salió mal. Probá de nuevo.';
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    resetMessages();
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      handleClose();
    } catch (err) {
      console.error(err);
      const msg = authErrorMessage(err);
      if (msg) setError(msg);
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (view === 'login') {
        await signInWithEmailAndPassword(auth, email.trim(), password);
        handleClose();
      } else if (view === 'register') {
        if (name.trim().length < 2) throw new Error('Escribí tu nombre completo.');
        const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        await updateProfile(userCredential.user, { displayName: name.trim() });
        handleClose();
      } else if (view === 'recover') {
        if (!email) throw new Error('Escribí tu correo.');
        const currentPath = window.location.pathname;

        const response = await fetch("https://enviarcorreorecuperacion-sux2wc5jja-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), returnUrl: currentPath })
        });

        const data = await response.json();

        if (response.ok) {
          setSuccessMessage('¡Listo! Revisá tu correo para crear una contraseña nueva.');
        } else {
          throw new Error(data.error || 'No pudimos enviar el correo. Probá de nuevo.');
        }
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error(err);
      setError(authErrorMessage(err) || '');
    }
    setLoading(false);
  };

  const busy = loading || googleLoading;
  const copy = COPY[view];
  const label = 'block text-[13px] font-semibold mb-1.5';

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="login-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[11000] flex items-end sm:items-center justify-center sm:p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={handleClose}
        >
          <motion.div
            initial={sheetHidden}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={sheetHidden}
            transition={sheetTransition}
            {...(isMobile ? {
              drag: 'y',
              dragControls,
              dragListener: false,
              dragConstraints: { top: 0, bottom: 0 },
              dragElastic: { top: 0, bottom: 1 },
              dragMomentum: false,
              onDragEnd: (_, info) => { if (info.offset.y > 110 || info.velocity.y > 600) handleClose(); },
            } : {})}
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-labelledby="login-title"
            className="w-full sm:max-w-[400px] max-h-[92dvh] overflow-y-auto no-scrollbar rounded-t-[28px] sm:rounded-[28px]"
            style={{ background: 'var(--cv-sheet, #fff)', color: 'var(--cv-ink, #15171A)', overscrollBehavior: 'contain' }}
          >
            {/* Encabezado — en mobile también es el asa para arrastrar */}
            <div onPointerDown={isMobile ? (e) => dragControls.start(e) : undefined}
              className="relative px-6 pt-7 pb-5 touch-none sm:touch-auto">
              <span aria-hidden="true" className="sm:hidden absolute top-2.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full"
                style={{ background: 'var(--cv-line-strong)' }} />

              <button type="button" onClick={handleClose} aria-label="Cerrar"
                className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                style={{ background: 'var(--cv-sunk)', color: 'var(--cv-ink-soft)' }}>
                <X size={18} />
              </button>

              {view === 'recover' ? (
                <button type="button" onClick={() => switchView('login')} aria-label="Volver a ingresar"
                  className="w-10 h-10 -ml-2 mb-2 rounded-full flex items-center justify-center active:scale-95 transition-transform">
                  <ArrowLeft size={20} />
                </button>
              ) : (
                <div className="w-12 h-12 mb-4 rounded-[14px] overflow-hidden flex items-center justify-center"
                  style={{ background: 'var(--cv-surface)', boxShadow: '0 0 0 1px var(--cv-line)' }}>
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="cv-display" style={{ color: brandColor, fontSize: 26 }}>{shopName?.charAt(0) || 'B'}</span>
                  )}
                </div>
              )}

              <h2 id="login-title" className="cv-display" style={{ fontSize: '2.75rem' }}>{copy.title}</h2>
              <p className="mt-1.5 text-[15px] pr-8" style={{ color: 'var(--cv-ink-soft)' }}>
                {view === 'login' && shopName ? `Ingresá para ver y manejar tus turnos en ${shopName}.` : copy.subtitle}
              </p>
            </div>

            <div className="px-6 pb-[calc(env(safe-area-inset-bottom)+24px)] sm:pb-6">

              {/* Selector Ingresar / Crear cuenta (control segmentado) */}
              {view !== 'recover' && (
                <div role="tablist" aria-label="Tipo de acceso" className="relative grid grid-cols-2 p-1 mb-5 rounded-full"
                  style={{ background: 'var(--cv-sunk)' }}>
                  {[['login', 'Ingresar'], ['register', 'Crear cuenta']].map(([key, text]) => {
                    const active = view === key;
                    return (
                      <button key={key} type="button" role="tab" aria-selected={active}
                        onClick={() => switchView(key)}
                        className="relative h-10 rounded-full text-[14px] font-semibold"
                        style={{ color: active ? 'var(--cv-ink)' : 'var(--cv-ink-soft)' }}>
                        {active && (
                          <motion.span layoutId="login-segment" aria-hidden="true"
                            className="absolute inset-0 rounded-full"
                            transition={reduceMotion ? { duration: 0 } : { type: 'spring', bounce: 0, duration: 0.3 }}
                            style={{ background: 'var(--cv-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 0 0 1px var(--cv-line)' }} />
                        )}
                        <span className="relative">{text}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Google primero: es el camino más rápido en el celular */}
              {view !== 'recover' && (
                <>
                  <motion.button type="button" onClick={handleGoogleLogin} disabled={busy}
                    whileTap={busy ? undefined : { scale: 0.98 }}
                    className="w-full h-14 rounded-2xl flex items-center justify-center gap-2.5 text-[16px] font-semibold disabled:opacity-60"
                    style={{ background: 'var(--cv-surface)', boxShadow: '0 0 0 1px var(--cv-line-strong)', color: 'var(--cv-ink)' }}>
                    {googleLoading
                      ? <Loader2 size={18} className="animate-spin" />
                      : <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="" />}
                    Continuar con Google
                  </motion.button>

                  <div className="my-5 flex items-center gap-3">
                    <div className="h-px flex-1" style={{ background: 'var(--cv-line)' }} />
                    <span className="text-[13px] font-medium" style={{ color: 'var(--cv-ink-faint)' }}>o con tu correo</span>
                    <div className="h-px flex-1" style={{ background: 'var(--cv-line)' }} />
                  </div>
                </>
              )}

              {/* Avisos */}
              <AnimatePresence initial={false}>
                {error && (
                  <motion.div key="error" {...revealProps}>
                    <div role="alert" className="mb-4 p-3.5 rounded-[14px] flex items-start gap-2.5 text-[14px] font-medium"
                      style={{ background: `${DANGER}14`, boxShadow: `0 0 0 1px ${DANGER}40`, color: DANGER }}>
                      <AlertTriangle size={17} className="shrink-0 mt-px" /> {error}
                    </div>
                  </motion.div>
                )}
                {successMessage && (
                  <motion.div key="success" {...revealProps}>
                    <div role="status" className="mb-4 p-3.5 rounded-[14px] flex items-start gap-2.5 text-[14px] font-medium"
                      style={{ background: `${OK}14`, boxShadow: `0 0 0 1px ${OK}40`, color: OK }}>
                      <CheckCircle size={17} className="shrink-0 mt-px" /> {successMessage}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <AnimatePresence initial={false}>
                  {view === 'register' && (
                    <motion.div key="name" {...revealProps}>
                      <label htmlFor="login-name" className={label}>Nombre</label>
                      <input id="login-name" type="text" required value={name} onChange={(e) => setName(e.target.value)}
                        className="cv-input" placeholder="Tu nombre y apellido"
                        autoComplete="name" autoCapitalize="words" enterKeyHint="next" />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label htmlFor="login-email" className={label}>Correo</label>
                  <input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="cv-input" placeholder="correo@ejemplo.com"
                    autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
                    enterKeyHint={view === 'recover' ? 'send' : 'next'} />
                </div>

                {view !== 'recover' && (
                  <div>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <label htmlFor="login-password" className="text-[13px] font-semibold">Contraseña</label>
                      {view === 'login' && (
                        <button type="button" onClick={() => switchView('recover')}
                          className="text-[13px] font-semibold" style={{ color: brandColor }}>
                          ¿La olvidaste?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input id="login-password" type={showPassword ? 'text' : 'password'} required minLength={6}
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="cv-input" style={{ paddingRight: 52 }}
                        placeholder={view === 'register' ? 'Mínimo 6 caracteres' : '••••••••'}
                        autoComplete={view === 'register' ? 'new-password' : 'current-password'}
                        enterKeyHint="go" />
                      <button type="button" onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ color: 'var(--cv-ink-faint)' }}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                )}

                <motion.button type="submit" disabled={busy} whileTap={busy ? undefined : { scale: 0.98 }}
                  className="w-full h-14 mt-1 rounded-2xl flex items-center justify-center gap-2 text-[16px] font-bold disabled:opacity-70"
                  style={{ background: brandColor, color: contrastText }}>
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {copy.cta}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
